import { CartAddEvent } from '@theme/events';

class IsabellaCartUpsells extends HTMLElement {
  connectedCallback() {
    // Se escucha en fase de captura en el documento: así ninguna app (sliders de imágenes, swatches…)
    // puede «tragarse» el clic en el botón + antes de que llegue aquí.
    document.addEventListener('click', this.onCaptureClick, true);
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCaptureClick, true);
    this.removeEventListener('change', this.onChange);
  }

  onCaptureClick = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const hit = event.target.closest(
      '[data-isa-upsell-prev],[data-isa-upsell-next],[data-isa-upsell-toggle],[data-isa-picker-close],[data-isa-picker-opt],[data-isa-upsell-add]'
    );
    if (!hit || !this.contains(hit)) return;
    event.preventDefault();
    event.stopPropagation();
    this.onClick(event);
  };

  onChange = (event) => {
    const select = event.target.closest('[data-isa-upsell-variant]');
    if (!select) return;

    const card = select.closest('[data-isa-upsell-card]');
    const button = card?.querySelector('[data-isa-upsell-add]');
    const option = select.options[select.selectedIndex];
    if (!card || !button || !option) return;

    button.dataset.variantId = option.value;

    const price = card.querySelector('[data-isa-upsell-price]');
    const compare = card.querySelector('[data-isa-upsell-compare]');
    const saving = card.querySelector('[data-isa-upsell-saving]');

    if (price) price.textContent = option.dataset.price || '';

    if (compare) {
      const value = option.dataset.compare || '';
      compare.textContent = value;
      compare.hidden = !value;
    }

    if (saving) {
      const value = option.dataset.saving || '';
      saving.textContent = value ? `Ahorras ${value}%` : '';
      saving.hidden = !value;
    }
  };

  onClick = async (event) => {
    const prev = event.target.closest('[data-isa-upsell-prev]');
    const next = event.target.closest('[data-isa-upsell-next]');

    if (prev || next) {
      const track = this.querySelector('[data-isa-upsell-track]');
      if (!track) return;
      const card = track.querySelector('[data-isa-upsell-card]');
      const amount = card ? card.getBoundingClientRect().width + 10 : 240;
      track.scrollBy({ left: prev ? -amount : amount, behavior: 'smooth' });
      return;
    }

    const toggle = event.target.closest('[data-isa-upsell-toggle]');
    if (toggle) {
      this.togglePicker(toggle);
      return;
    }

    if (event.target.closest('[data-isa-picker-close]')) {
      this.closePicker();
      return;
    }

    const opt = event.target.closest('[data-isa-picker-opt]');
    if (opt) {
      if (opt.dataset.soldout !== undefined && opt.getAttribute('aria-pressed') !== 'true') {
        // Se permite elegir, pero el botón quedará desactivado si la combinación no existe
      }
      this.selectOption(opt);
      return;
    }

    const button = event.target.closest('[data-isa-upsell-add]');
    if (!button || button.disabled) return;

    const id = button.dataset.variantId;
    if (!id) return;

    button.disabled = true;
    const hasIcon = !!button.querySelector('svg');
    const original = hasIcon ? button.innerHTML : button.textContent;
    if (!hasIcon) button.textContent = '…';

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const formData = new FormData();
      formData.append('id', id);
      formData.append('quantity', '1');
      if (sectionIds.length) formData.append('sections', [...new Set(sectionIds)].join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'No se pudo añadir');

      this.dispatchEvent(new CartAddEvent({}, id.toString(), {
        source: 'isabella-cart-upsell',
        itemCount: 1,
        variantId: id.toString(),
        sections: data.sections || {},
      }));
    } catch (error) {
      console.error(error);
      if (!hasIcon) button.textContent = 'Error';
      setTimeout(() => {
        if (hasIcon) button.innerHTML = original;
        else button.textContent = original;
        button.disabled = false;
      }, 1400);
      return;
    }

    if (!hasIcon) button.textContent = '✓';
  };
}

IsabellaCartUpsells.prototype.closePicker = function () {
  const panel = this.querySelector('[data-isa-upsell-picker]');
  if (panel) {
    panel.hidden = true;
    panel.innerHTML = '';
  }
  this.querySelectorAll('[data-isa-upsell-toggle]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
  this.querySelectorAll('[data-isa-upsell-card][data-active]').forEach((el) => el.removeAttribute('data-active'));
  this._picker = null;
};

IsabellaCartUpsells.prototype.togglePicker = function (toggle) {
  const card = toggle.closest('[data-isa-upsell-card]');
  const panel = this.querySelector('[data-isa-upsell-picker]');
  const json = card?.querySelector('[data-isa-upsell-json]');
  if (!card || !panel || !json) return;
  const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
  this.closePicker();
  if (wasOpen) return;

  let data;
  try {
    data = JSON.parse(json.textContent);
  } catch (e) {
    return;
  }

  const esc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const first = data.variants.find((v) => v.available) || data.variants[0];
  const last = data.options.length - 1;
  // Preselecciona todo menos la última opción (normalmente la talla)
  const selected = data.options.map((_, i) => (i < last ? first.options[i] : null));
  if (data.options.length === 1 && data.variants.filter((v) => v.available).length === 1) selected[0] = first.options[0];

  const groups = data.options
    .map((name, i) => {
      const values = [...new Set(data.variants.map((v) => v.options[i]))];
      const buttons = values
        .map(
          (val) =>
            `<button type="button" class="isa-up-picker__opt" data-isa-picker-opt data-index="${i}" data-value="${esc(val)}" aria-pressed="${selected[i] === val}">${esc(val)}</button>`
        )
        .join('');
      return `<div class="isa-up-picker__group"><p class="isa-up-picker__label">${esc(name)}</p><div class="isa-up-picker__values">${buttons}</div></div>`;
    })
    .join('');

  panel.innerHTML = `
    <div class="isa-up-picker__head">
      ${data.image ? `<img class="isa-up-picker__thumb" src="${esc(data.image)}" alt="" width="44" height="44">` : ''}
      <div class="isa-up-picker__info">
        <p class="isa-up-picker__title">${esc(data.title)}</p>
        <span class="isa-up-picker__price" data-isa-picker-price>${esc(first.price)}</span>
      </div>
      <button type="button" class="isa-up-picker__close" data-isa-picker-close aria-label="Cerrar">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
    </div>
    ${groups}
    <button type="button" class="isa-up-picker__add" data-isa-upsell-add data-isa-picker-add disabled>Elige ${esc(data.options[last]).toLowerCase()}</button>`;

  this._picker = { data, selected };
  card.setAttribute('data-active', '');
  toggle.setAttribute('aria-expanded', 'true');
  panel.hidden = false;
  this.refreshPicker();
  panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
};

IsabellaCartUpsells.prototype.selectOption = function (opt) {
  if (!this._picker) return;
  const i = Number(opt.dataset.index);
  this._picker.selected[i] = opt.dataset.value;
  this.refreshPicker();
};

IsabellaCartUpsells.prototype.refreshPicker = function () {
  const panel = this.querySelector('[data-isa-upsell-picker]');
  if (!panel || !this._picker) return;
  const { data, selected } = this._picker;
  const last = data.options.length - 1;

  panel.querySelectorAll('[data-isa-picker-opt]').forEach((btn) => {
    const i = Number(btn.dataset.index);
    const val = btn.dataset.value;
    btn.setAttribute('aria-pressed', selected[i] === val ? 'true' : 'false');
    // Agotado = ninguna variante disponible con este valor y el resto de opciones elegidas
    const possible = data.variants.some(
      (v) => v.available && v.options[i] === val && v.options.every((o, k) => k === i || selected[k] == null || selected[k] === o)
    );
    if (possible) btn.removeAttribute('data-soldout');
    else btn.setAttribute('data-soldout', '');
  });

  const variant = data.variants.find((v) => v.options.every((o, k) => selected[k] === o));
  const add = panel.querySelector('[data-isa-picker-add]');
  const price = panel.querySelector('[data-isa-picker-price]');
  if (variant && price) price.textContent = variant.price;
  if (!add) return;
  if (variant && variant.available) {
    add.disabled = false;
    add.dataset.variantId = variant.id;
    add.textContent = 'Añadir al carrito';
  } else {
    add.disabled = true;
    delete add.dataset.variantId;
    add.textContent = variant ? 'Agotado' : `Elige ${String(data.options[selected.findIndex((s) => s == null)] ?? data.options[last]).toLowerCase()}`;
  }
};

if (!customElements.get('isabella-cart-upsells')) {
  customElements.define('isabella-cart-upsells', IsabellaCartUpsells);
}
